import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import { useMapFullscreen } from "../../context/MapFullscreenContext";
import SinConexionKawaii from "../SinConexionKawaii";

// Template144: Día 1 - Mapa final del Día 1 (Gyeongbokgung y Bukchon)
// Adaptado de guia-seul PageDia1Mapa.jsx
const Template144 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const [loaded, setLoaded] = useState(true);
	const [fullscreen, setFullscreen] = useState(false);
	const [isOnline, setIsOnline] = useState(
		typeof navigator !== "undefined" ? navigator.onLine : true,
	);
	const [stickerError, setStickerError] = useState(false);
	const { setMapFullscreen } = useMapFullscreen() || {};

	useEffect(() => {
		if (typeof setMapFullscreen === "function") {
			setMapFullscreen(fullscreen);
			return () => setMapFullscreen(false);
		}
	}, [fullscreen, setMapFullscreen]);

	useEffect(() => {
		if (typeof navigator === "undefined") return;
		setIsOnline(navigator.onLine);
		const handleOnline = () => {
			setIsOnline(true);
			setLoaded(true);
		};
		const handleOffline = () => {
			setIsOnline(false);
			setLoaded(true);
		};
		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);
		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, []);

	useEffect(() => {
		const style = document.createElement("style");
		style.innerHTML = `
			@keyframes spin {
				0% { transform: rotate(0deg); }
				100% { transform: rotate(360deg); }
			}
		`;
		document.head.appendChild(style);
		return () => document.head.removeChild(style);
	}, []);

	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	// Misma forma que Viewer/StickerApp: leer page con data aplanado a la raíz
	const p = page?.data != null ? { ...page, ...page.data } : page;
	const headerText = p?.texts?.[0]?.content;
	const mapUrl = p?.maps?.[0];
	const tipBoxText = p?.texts?.[1]?.content;
	const bottomText = p?.texts?.[2]?.content;

	const getCanvasImageUrl = (id) => {
		const canvasImages = p?.canvasImages ?? p?.data?.canvasImages;
		if (canvasImages && Array.isArray(canvasImages)) {
			const found = canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const getImageSrc = (id) => {
		const images = p?.images ?? p?.data?.images;
		if (images && Array.isArray(images)) {
			const found = images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const stickerUrl = getCanvasImageUrl("dia1-mapa-sticker");
	const logoImage = getCanvasImageUrl("logo");

	const actualHeaderColor = headerBackgroundColor || "#2E8B80";
	const contentColor = textColor || "#444";

	if (fullscreen && mapUrl) {
		return (
			<Box
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "#fff",
					zIndex: 9999,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Box
					sx={{
						padding: "10px",
						textAlign: "right",
						backgroundColor: "#2E8B80",
					}}
				>
					<button
						onClick={() => setFullscreen(false)}
						style={{
							backgroundColor: "#fff",
							color: "#24221a",
							border: "none",
							borderRadius: "8px",
							padding: "6px 12px",
							fontWeight: 600,
							cursor: "pointer",
							fontFamily: fontFamily || "'Poppins', sans-serif",
						}}
					>
						← Volver a la guía
					</button>
				</Box>
				<iframe
					title="Mapa grande"
					src={mapUrl}
					width="100%"
					height="100%"
					style={{ border: 0, flex: 1 }}
					allowFullScreen
					loading="lazy"
				/>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			{headerText && (
				<Box
					data-block-id="header"
					data-template-header
					sx={{
						backgroundColor: actualHeaderColor,
						color: headerTextColor || "#fff",
						textAlign: "center",
						padding: "15px 0",
						fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
						fontSize: "14px",
						fontWeight: 800,
						flexShrink: 0,
						position: "sticky",
						top: 0,
						zIndex: 99999,
					}}
				>
					{headerText}
					{logoImage && !isSmallScreen && (
						<Box
							component="img"
							src={logoImage}
							alt="logo"
							sx={{
								width: 80,
								position: "absolute",
								right: 2,
								top: 18,
								zIndex: 999999,
							}}
						/>
					)}
				</Box>
			)}

			{/* Contenedor con altura definida (minHeight + flex) para que el iframe height:100% resuelva igual en Viewer y StickerApp */}
			<Box
				data-block-id="map"
				sx={{
					flex: 1,
					marginTop: "-70px",
					position: "relative",
					minHeight: 320,
				}}
			>
				{/* Wrapper con position absolute inset: así el iframe tiene siempre una altura de referencia */}
				<Box
					sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}
				>
					{!loaded && isOnline && (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								height: "100%",
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								zIndex: 1,
							}}
						>
							<Box
								sx={{
									width: 20,
									height: 20,
									border: "4px solid #ccc",
									borderTop: "4px solid #2E8B80",
									borderRadius: "50%",
									animation: "spin 1s linear infinite",
								}}
							/>
						</Box>
					)}

					{isOnline && mapUrl && (
						<button
							onClick={() => setFullscreen(true)}
							style={{
								position: "absolute",
								top: 80,
								right: 10,
								backgroundColor: "#2E8B80",
								color: "#fff",
								padding: "8px 10px",
								fontSize: "11px",
								borderRadius: "8px",
								fontFamily: fontFamily || "'Poppins', sans-serif",
								boxShadow: "1px 1px 2px rgba(0,0,0,0.2)",
								zIndex: 4,
								border: "none",
								cursor: "pointer",
							}}
						>
							Ver mapa grande
						</button>
					)}

					{isOnline && mapUrl ? (
						<iframe
							title="Mapa Día 1"
							src={mapUrl}
							width="100%"
							height="100%"
							style={{
								border: 0,
								display: "block",
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
							}}
							onLoad={() => setLoaded(true)}
						/>
					) : !isOnline ? (
						<SinConexionKawaii height="100%" fontFamily={fontFamily} />
					) : null}
				</Box>

				{stickerUrl && !stickerError && (
					<img
						src={stickerUrl}
						alt=""
						onError={() => setStickerError(true)}
						style={{
							width: 140,
							position: "absolute",
							bottom: -20,
							left: -18,
							zIndex: 3,
						}}
					/>
				)}

				{tipBoxText && (
					<Box
						sx={{
							position: "absolute",
							bottom: 60,
							left: 70,
							backgroundColor: "#fff8db",
							border: "2px solid #f5c400",
							borderRadius: "16px 16px 16px 0px",
							padding: "10px 12px",
							fontSize: 11,
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
							color: contentColor,
							boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
							maxWidth: 170,
							zIndex: 3,
						}}
					>
						{tipBoxText}
					</Box>
				)}
			</Box>

			{bottomText && (
				<Box
					data-block-id="text-1"
					sx={{
						padding: "12px 16px",
						fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
						fontSize: 12,
						backgroundColor: "#fff",
						color: "#333",
					}}
				>
					{bottomText}
				</Box>
			)}
		</Box>
	);
};

Template144.thumbnail = "/images/template-1.png";

export default Template144;
